import {isNil} from 'lodash';
import {SchemeLink} from '../../../applink/SchemeLink';
import {t} from '../../../common/getTranslations';
import {twMerge} from '../../../common/twMerge';
import {Button} from '../../atoms/Button/Button';
import {ButtonColorScheme, ButtonSize} from '../../atoms/Button/Button.enum';
import {BADGE_TYPE_TO_ICON_TYPE_MAP} from '../../atoms/Icon/constants';
import {Icon} from '../../atoms/Icon/Icon';
import {IconType} from '../../atoms/Icon/IconType.enum';
import {SeparatorType} from '../../atoms/Separator/SeparatorType.enum';
import {Text} from '../../atoms/Text/Text';
import {CheckBox} from '../../molecules/CheckBox/CheckBox';
import {LabelBadge} from '../../molecules/LabelBadge/LabelBadge';
import {LabelBadge16Type} from '../../molecules/LabelBadge/LabelBadge16Type.enum';
import {LabelBadgeSizeType} from '../../molecules/LabelBadge/LabelBadgeSizeType.enum';
import {TextsWithSeparator} from '../../molecules/TextsWithSeparator/TextsWithSeparator';
import {ThemeBadge} from '../../molecules/ThemeBadge/ThemeBadge';
import {ThumbnailType} from '../../molecules/ThumbnailWithBadges/ThumbnailType.enum';
import {ThumbnailWithBadges} from '../../molecules/ThumbnailWithBadges/ThumbnailWithBadges';
import {Metadata} from '../Metadata/Metadata';
import {NormalListViewItemProps} from './props.interface';
import {RightButtonType} from './RightButtonType.enum';

/**
 * 일반리스트. 일반적으로 작품을 표현할 때 쓰입니다.
 * 메인, 검색, 선물함, 보관함, 오늘의 신작, 오늘의 UP, 테마 키워드,
 */
export function NormalListViewItem({
  isAgeRestricted,
  item: {
    altText,
    thumbnail,
    showPlayerIcon,
    isCheckMode,
    isChecked,
    badgeList,
    ageGradeBadge,
    statusBadge,
    statusLabelText,
    row1,
    row2,
    row3,
    row4,
    row5,
    scheme,
    rank,
    isHelixGift,
    price,
    discountPrice,
    discountRate,
    discountRateText,
  },
  rightButton,
  imageLazyLoad,
  onClick,
  onClickCapture,
  onClickRightButton,
  onClickContinueView,
  className,
  borderRadius,
  isSet,
  ...rest
}: NormalListViewItemProps) {
  const isViewerScheme = scheme?.includes('viewer');
  const NormalListItemLink = isViewerScheme ? 'div' : SchemeLink;

  return (
    <div
      {...rest}
      className={twMerge('flex justify-between px-15pxr py-5pxr', className)}
    >
      {isCheckMode && (
        <div className="mr-14pxr flex shrink-0 items-center">
          <CheckBox checked={isChecked} />
        </div>
      )}
      <NormalListItemLink
        scheme={scheme || undefined}
        {...(!isViewerScheme && {linkClassName: 'cursor-pointer flex-1'})}
        className="flex-1 cursor-pointer"
        onClick={onClick}
        onClickCapture={onClickCapture}
      >
        <div className="flex items-center" aria-label={altText || ''}>
          <ThumbnailWithBadges
            borderRadius={borderRadius}
            thumbnailType={ThumbnailType.Portrait}
            thumbnail={thumbnail}
            badgeList={badgeList}
            ageGradeBadge={ageGradeBadge}
            isAgeRestricted={isAgeRestricted}
            extraInfoIcon={
              showPlayerIcon ? IconType.PlayerPlaySolidStatic : undefined
            }
            imageLazyLoad={imageLazyLoad}
            className="mr-16pxr w-80pxr shrink-0"
            aria-hidden={true}
            discountRateText={discountRateText}
          />
          <div className="flex flex-col" aria-hidden={true}>
            {rank && (
              <Text className="font-large3-bold mb-6pxr text-theme-solid-100">
                {rank}
              </Text>
            )}
            {row1 && (
              <Text className="font-medium2 line-clamp-2 pb-2pxr text-theme-solid-100 break-word-anywhere">
                {statusBadge && (
                  <Icon
                    className="mr-4pxr inline-block align-middle"
                    type={BADGE_TYPE_TO_ICON_TYPE_MAP[statusBadge]}
                    disableActiveEffect
                  />
                )}
                {statusLabelText && (
                  <LabelBadge
                    className="mr-4pxr inline-flex align-middle"
                    sizeType={LabelBadgeSizeType.Size16}
                    type={LabelBadge16Type.red}
                    label={statusLabelText}
                  />
                )}

                {isSet && (
                  <ThemeBadge
                    className="mr-4pxr inline py-2pxr"
                    isPrimary={true}
                    label={t('sethome_badge_set')}
                  />
                )}
                <span>{row1 || ''}</span>
              </Text>
            )}
            {row2 && (
              <TextsWithSeparator
                texts={row2}
                className="font-small2 mb-12pxr text-theme-solid-60"
                separatorType={SeparatorType.Dot}
              />
            )}
            {row3 && (
              <Metadata
                texts={row3.metaList}
                className="pb-4pxr"
                separatorType={SeparatorType.Dot}
                subtitleClassName="font-small2 text-theme-solid-60 opacity-100"
              />
            )}
            {row4 && (
              <TextsWithSeparator
                texts={row4}
                separatorClassName="mx-4pxr bg-theme-transparent-20 h-8pxr"
                className="font-small2 line-clamp-1 text-ellipsis break-all text-theme-solid-100"
                separatorType={SeparatorType.Line}
              />
            )}
            {row5 && (
              <Text className="font-small2 line-clamp-1 text-ellipsis break-all text-theme-solid-100">
                {row5}
              </Text>
            )}
            {!isNil(price) && !isNil(discountRate) && (
              <div className="flex">
                <Text
                  className={twMerge(
                    'font-small2',
                    discountRate
                      ? 'text-theme-solid-60 line-through'
                      : 'text-theme-solid-100'
                  )}
                >
                  {discountRate
                    ? price?.toLocaleString()
                    : t('common_cash_amount', {
                        values: {value: price.toLocaleString()},
                      })}
                </Text>
                {discountRate > 0 && !isNil(discountPrice) && (
                  <Text className="font-small2 ml-4pxr text-static-point-red">
                    {t('common_cash_amount', {
                      values: {value: discountPrice.toLocaleString()},
                    })}
                  </Text>
                )}
              </div>
            )}
          </div>
        </div>
      </NormalListItemLink>
      {rightButton && (
        <div
          className="ml-8pxr flex shrink-0 cursor-pointer items-center"
          tabIndex={0}
        >
          {rightButton.type === RightButtonType.Gift && (
            <>
              {rightButton.isActive && (
                <SchemeLink scheme={scheme || undefined}>
                  <Button
                    size={ButtonSize.Small}
                    isRound={rightButton.rounded}
                    colorScheme={
                      isHelixGift
                        ? ButtonColorScheme['sp_2nd']
                        : rightButton.rounded
                          ? ButtonColorScheme['4st']
                          : ButtonColorScheme['3rd']
                    }
                    onClick={onClickContinueView}
                  >
                    {t('giftbox_gift_view')}
                  </Button>
                </SchemeLink>
              )}
              {!rightButton.isActive && (
                <Button
                  size={ButtonSize.Small}
                  isRound={rightButton.rounded}
                  colorScheme={
                    isHelixGift
                      ? ButtonColorScheme['sp']
                      : rightButton.rounded
                        ? ButtonColorScheme['2nd']
                        : ButtonColorScheme['1st']
                  }
                  onClick={onClickRightButton}
                >
                  {t('giftbox_gift_get')}
                </Button>
              )}
            </>
          )}
          {rightButton.type === RightButtonType.Alarm && (
            <Icon
              onClick={onClickRightButton}
              type={
                rightButton.isActive
                  ? IconType.NavigationAlarmOn
                  : IconType.NavigationAlarmOff
              }
              aria-label={'작품 업데이트 알림 설정'}
              aria-hidden={false}
            />
          )}
          {rightButton.type === RightButtonType.Continue && (
            <SchemeLink scheme={rightButton.scheme}>
              <div className="flex items-center" onClick={onClickContinueView}>
                <Text className="font-x-small1 pt-1pxr text-theme-solid-100">
                  {t('library_content_continue')}
                </Text>
                <Icon type={IconType.ListArrowRight} />
              </div>
            </SchemeLink>
          )}
          {rightButton.type === RightButtonType.NextProduct && (
            <SchemeLink scheme={rightButton.scheme}>
              <div className="flex items-center" onClick={onClickContinueView}>
                <Text className="font-x-small1 pt-1pxr text-theme-solid-100">
                  {t('library_content_next')}
                </Text>
                <Icon type={IconType.ListArrowRight} />
              </div>
            </SchemeLink>
          )}
        </div>
      )}
    </div>
  );
}
